import { Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { useEffect, useState } from 'react';
import type { FileArea, FileRefDto } from '@mexp/shared';
import { FileUploader } from '@/features/files/FileUploader';

export interface ReasonResult {
  reason: string;
  fileIds: string[];
}

/** Asks for a reason (required unless `optional`) and, optionally, supporting files. */
export function ReasonModal({
  opened,
  onClose,
  title,
  description,
  label = 'Reason',
  placeholder,
  confirmLabel,
  confirmColor = 'orange',
  fileArea,
  optional = false,
  loading,
  onSubmit,
}: {
  opened: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  label?: string;
  placeholder?: string;
  confirmLabel: string;
  confirmColor?: string;
  fileArea?: FileArea;
  optional?: boolean;
  loading?: boolean;
  onSubmit: (result: ReasonResult) => void;
}) {
  const [reason, setReason] = useState('');
  const [files, setFiles] = useState<FileRefDto[]>([]);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (opened) {
      setReason('');
      setFiles([]);
      setTouched(false);
    }
  }, [opened]);

  const missing = !optional && !reason.trim();
  return (
    <Modal opened={opened} onClose={onClose} title={title} size="lg">
      <Stack gap="md">
        {description && (
          <Text fz="sm" c="dimmed">
            {description}
          </Text>
        )}
        <Textarea
          label={label}
          placeholder={placeholder}
          autosize
          minRows={3}
          maxRows={8}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          onBlur={() => setTouched(true)}
          error={touched && missing ? 'Please add a short explanation' : undefined}
          withAsterisk={!optional}
          data-autofocus
        />
        {fileArea && (
          <Stack gap={4}>
            <Text fz="sm" fw={500}>
              Supporting files (optional)
            </Text>
            <FileUploader area={fileArea} value={files} onChange={setFiles} onBusyChange={setBusy} label="Attach marked-up copies or notes" />
          </Stack>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color={confirmColor}
            loading={loading}
            disabled={busy}
            onClick={() => {
              setTouched(true);
              if (!missing) onSubmit({ reason: reason.trim(), fileIds: files.map((f) => f.id) });
            }}
          >
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
